import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Body, BodyType, BodyColor, Heading, TypographyType, TypographyColor } from '../../../ui-elements';
import { PresenceToolTip } from '../../dashboard';

interface PresenceType {
    icon: string;
    name: string;
    properties: {
        count: number;
        text?: string[];
    };
}

interface GeoNameCellProps {
    geoName: string;
    presenceTypes: PresenceType[] | null;
    isFilteredOut?: boolean;
    showPresence?: boolean;
    onClick?: () => void;
}

export const GeoNameCell: React.FC<GeoNameCellProps> = ({
    geoName,
    presenceTypes,
    isFilteredOut = false,
    showPresence = true,
    onClick
}) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    return (
        <div className="d-flex align-items-center gap-2 w-100">
            {/* Geo Name - Clickable */}
            <Heading
                title={geoName}
                type={TypographyType.h5}
                colour={TypographyColor.primary}
                classname="text-underline cursor-pointer p-0 m-0"
                style={{ whiteSpace: 'nowrap' }}
                onClick={onClick}
            />

            {/* Presence Type Chips */}
            {showPresence && presenceTypes && presenceTypes.length > 0 && (
                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    {presenceTypes.map((presence, index) => (
                        <PresenceChip
                            key={index}
                            presence={presence}
                            isHovered={hoveredIndex === index}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// Separate component for each presence chip with tooltip
interface PresenceChipProps {
    presence: PresenceType;
    isHovered: boolean;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
}

const PresenceChip: React.FC<PresenceChipProps> = ({
    presence,
    isHovered,
    onMouseEnter,
    onMouseLeave
}) => {
    const chipRef = useRef<HTMLDivElement>(null);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (isHovered && chipRef.current) {
            const rect = chipRef.current.getBoundingClientRect();
            setTooltipPosition({
                top: rect.top - 10,
                left: rect.left + rect.width / 2
            });
        }
    }, [isHovered]);

    return (
        <div
            ref={chipRef}
            className="d-flex align-items-center justify-content-center gap-1 rounded"
            style={{
                backgroundColor: 'rgba(173, 175, 177, 0.15)',
                height: '23px',
                padding: '0 6px',
                minWidth: '43px',
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {/* Icon */}
            <img
                src={presence.icon}
                alt={presence.name || 'presence'}
                style={{ height: '1.5vh' }}
            />

            {/* Count */}
            <Body
                type={BodyType.p3}
                color={BodyColor.primary}
                classname="p-0 m-0"
            >
                {presence.properties?.count}
            </Body>

            {/* Tooltip on Hover - Rendered via Portal */}
            {isHovered && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                        transform: 'translate(-50%, -100%)',
                        zIndex: 9999
                    }}
                >
                    <div
                        className='position-relative bg-white text-center text-nowrap fs-15'
                        style={{
                            padding: '10px 14px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        <PresenceToolTip presenceData={presence} />

                        {/* Tooltip Arrow */}
                        <div
                            style={{
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                                borderTop: '6px solid white',
                            }}
                        />
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};
